
import { lookupCustomer, lookupDriver, lookupService, searchJobs, searchJobsPDF } from '@/api';
import { blobDownloader } from '@/utils/Common';
import JobTable from '@/components/dashBoard/jobTable/JobTable.vue';
import Autocomplete from '@/components/searchPage/components/Autocomplete/Autocomplete.vue';
import StateSelect from '@/components/searchPage/components/StateSelect/StateSelect.vue';
import DatePicker from '@/components/searchPage/DatePicker.vue';
import DateRangeCustom from '@/components/searchPage/DateRangeCustom.vue';
import Input from '@/components/searchPage/Input.vue';
import InputText from '@/components/searchPage/InputText.vue';
import InputSelected from '@/components/searchPage/InputSelected.vue';
import { Header, IGetJobs, ISearchSelected, Job, User } from '@/models/main';
import * as Sentry from '@sentry/vue';
import { addMonths, isValid } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import Button from '@/components/UI/button/Button.vue';
import Chip from '@/components/UI/chip/Chip.vue';
import Icon from '@/components/UI/Icon.vue';
import Tooltip from '@/components/UI/Tooltip.vue';

import dp from 'date-fns/parse';

import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { notificationErrorText, timeZoneDefault } from '@/utils/PortalConstants';

import JobCompliance from '@/views/JobBooking/JobCompliance.vue';

@Component({
  components: {
    Button,
    Chip,
    Icon,
    Tooltip,
    JobTable,
    DatePicker,
    DateRangeCustom,
    Input,
    InputText,
    InputSelected,
    Autocomplete,
    StateSelect,
    JobCompliance,
  },
  computed: {
    ...mapState(['availableStates']),
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class SearchPage extends Vue {
  public advanced = false;
  public user!: User;
  public dates: string[] = [];
  public datesIdSearch: string[] = [];
  public searchedJobs: Job[] = [];
  public loading = false;
  public loadingPdf = false;
  private timeout: ReturnType<typeof setTimeout>;
  public isLimited = false;
  public numberOfJobs = 0;
  public headers: Header[] = [
    { text: 'Id', value: 'id', align: 'left' },
    { text: 'Date', value: 'job_date', align: 'left' },
    { text: 'Customer Code', value: 'customer_code', align: 'left' },
    { text: 'Reference 1', value: 'ref1', align: 'left' },
    { text: 'Suburb Name', value: 'stop2_name', align: 'left' },
    { text: 'Suburb State', value: 'suburb_state2', align: 'left' },
    { text: 'Stop Number', value: 'main_stop', align: 'left' },
    { text: 'Driver', value: 'driver2', align: 'left' },
    { text: 'Service code', value: 'cost_service', align: 'left' },
    { text: 'Warehouse', value: 'wh_status', align: 'left', sortable: false },
    { text: 'Attributes', value: 'attributes', align: 'left', sortable: false },
    { text: 'Status', value: 'status', align: 'left', sortable: false },
    { text: 'Actions', value: 'actions', align: 'left custom' },
  ];
  public autocompleteDriver = lookupDriver;
  public autocompleteCustomer = lookupCustomer;
  public autocompleteService = lookupService;

  public modalMupltipleSearch = {
    value: '',
    isOpen: false,
    linesCounter: 0,
    items: [],
    radio: 0,
    types: [
      {
        jobIDs: {
          name: 'Job IDs',
          type: 'default',
        },
      },
      {
        serialNumbers: {
          name: 'Serial Numbers',
          type: 'single',
        },
      },
      {
        mixed: {
          name: 'Combined data',
          type: 'single',
        },
      },
      {
        mixedAddress: {
          name: 'Combined address',
          type: 'single',
        },
      },
    ],
  };

  /**
   * Updates the searchedJobs array with the sorted data from the table
   * to ensure that JobCompliance modal shows jobs in the same order as the table
   */
  public handleSortedItemsChanged(sortedItems: Job[]): void {
    if (sortedItems && sortedItems.length > 0) {
      this.searchedJobs = [...sortedItems];
    }
  }

  public multipleInputChanged(value) {
    const lines = value.split(/\r|\r\n|\n/);
    this.modalMupltipleSearch.linesCounter = lines.length;
    this.modalMupltipleSearch.items = [];
    if (this.modalMupltipleSearch.linesCounter === 1) {
      this.modalMupltipleSearch.items = value.split(',').filter(x => x.trim() !== '');
    } else {
      this.modalMupltipleSearch.items = lines.filter(x => x.trim() !== '');
    }
  }

  public modalMupltipleSearchSubmit(): void {
    const chosenType = this.modalMupltipleSearch.types[this.modalMupltipleSearch.radio];
    const model = Object.keys(chosenType)[0];
    const modelType = Object.values(chosenType)[0].type;

    if (this.selected[model] || this.selectedSingleInputs[model]) {
      this.modalMupltipleSearch.items
        .filter(x => x !== '')
        .map(item => {
          if (modelType === 'single') {
            if (!this.selectedSingleInputs[model].data.includes(item)) {
              this.selectedSingleInputs[model].data.push(item);
            }
          } else {
            if (!this.selected[model].data.includes(item)) {
              this.selected[model].data.push(item);
            }
          }
        });

      if (modelType === 'single') {
        this.find();
      }

      this.modalMupltipleSearch.isOpen = false;
      this.modalMupltipleSearch.value = '';
      this.modalMupltipleSearch.items = [];
      this.modalMupltipleSearch.radio = 0;
    }
  }

  // Compliance modal
  public showModal = false;
  public currentJobIdx = -1;

  public currentJobChanged(idx: number): void {
    this.currentJobIdx = idx;
  }

  public closeModal(): void {
    this.showModal = false;
    this.currentJobIdx = -1;
    this.$forceUpdate();
  }

  public openInEditModal(unique_id: string): void {
    this.showModal = true;
    this.currentJobIdx = this.searchedJobs.findIndex(x => x.unique_id === unique_id);
  }
  // / Compliance modal

  availableFilters = [
    this.$ability.can('staff', 'Job') ? "Can't schedule" : null, // hide for non-staff users
    'Requested for change',
    'Only Active',
    'Not Allocated',
  ];
  private selectedFilters: string[] = [];

  selected: Record<string, ISearchSelected> = {
    jobIDs: {
      name: 'Job IDs',
      data: [],
    },
    driverIDs: {
      name: 'Driver IDs',
      data: [],
    },
    customerCodes: {
      name: 'Customer codes',
      data: [],
    },
    serviceCodes: {
      name: 'Service codes',
      data: [],
    },
    stateName: {
      name: 'State',
      data: [],
    },
    filters: {
      name: 'Filters',
      data: [],
    },
  };

  selectedSingleInputs: Record<string, ISearchSelected> = {
    serialNumbers: {
      name: 'Serial numbers',
      data: [],
    },
    payOnHold: {
      name: 'Pay On Hold',
      data: [],
    },
    mixed: {
      name: 'Mixed',
      data: [],
    },
    mixedAddress: {
      name: 'Mixed Address',
      data: [],
    },
  };

  selectedSingleInputsCloned = { ...this.selectedSingleInputs };

  @Watch('selected', { deep: true })
  private async watchToSelected() {
    const selectsHaveNoData = this.checkSelectsHaveNoData({
      ...this.selected,
      ...this.selectedSingleInputs,
    });
    if (selectsHaveNoData) {
      this.searchedJobs = [];
      this.loading = false;
      this.numberOfJobs = 0;
      this.isLimited = false;
    } else {
      if (!this.loading) {
        await this.find();
      }
    }
  }

  // Single Inputs:
  @Watch('selectedSingleInputs', { deep: true })
  private async watchToSelectedSingleInputs() {
    // Make clone of selectedSingleInputs to detect changes in children array
    this.selectedSingleInputsCloned = structuredClone(this.selectedSingleInputs);
  }

  // Single Inputs Clone:
  @Watch('selectedSingleInputsCloned', { deep: true })
  private watchToSelectedSingleInputsCloned(
    nextValue: Record<string, ISearchSelected>,
    prevValue: Record<string, ISearchSelected>,
  ) {
    if (this.advanced) {
      // Calculate prev & next value length
      const countInputsLength = (selectArray: Record<string, ISearchSelected>): number => {
        return Object.values(selectArray)
          .map((item: ISearchSelected) => item.data)
          .reduce((acc, item) => {
            return acc + item.length;
          }, 0);
      };
      const prevValueLength = countInputsLength(prevValue);
      const nextValueLength = countInputsLength(nextValue);
      // Update immediate when adding, set delay for removing
      const shouldUpdateImmediate = prevValueLength < nextValueLength;

      if (!this.loading) {
        const selectsHaveNoData = this.checkSelectsHaveNoData({
          ...this.selected,
          ...this.selectedSingleInputs,
        });
        if (selectsHaveNoData) {
          this.searchedJobs = [];
          this.loading = false;
          this.numberOfJobs = 0;
          this.isLimited = false;
        } else {
          this.find(shouldUpdateImmediate);
        }
      }
    }
  }

  @Watch('dates', { deep: true })
  private watchToDates() {
    if (!this.loading) {
      this.rulesForFind();
    }
  }

  private checkSelectsHaveNoData(data: Record<string, ISearchSelected>) {
    delete data.filters;
    const chipsSum = Object.values(data)
      .map(item => item.data.length)
      .reduce((partialSum, a) => partialSum + a, 0);
    return chipsSum === 0;
  }

  // rules for automatic find
  private async rulesForFind(immediate?: boolean) {
    const selectsHaveNoData = this.checkSelectsHaveNoData({
      ...this.selected,
      ...this.selectedSingleInputs,
    });
    if (this.selected.jobIDs.data.length > 0 || (this.dates.length === 2 && !selectsHaveNoData)) {
      await this.find(immediate);
    } else {
      this.loading = false;
    }
  }

  public clearDateFilter() {
    this.dates = [];
  }

  private setDefaultDatesRange(): void {
    // Default is +/- 6 month from today
    const userTimeZone = this.user.timezone || timeZoneDefault;
    const fromDate = format(utcToZonedTime(addMonths(new Date(), -6), userTimeZone), 'yyyy-MM-dd'); // - 6 month
    const toDate = format(utcToZonedTime(addMonths(new Date(), 6), userTimeZone), 'yyyy-MM-dd'); // + 6 month
    this.datesIdSearch = [fromDate, toDate];
  }

  public clearDatesIdSearch(): void {
    this.setDefaultDatesRange();
  }

  private buildSearchParams(): Record<string, string> {
    const filtersCodes: string[] = [];
    this.selected.filters.data.map(filter => {
      filter === "Can't schedule" ? filtersCodes.push('CNTSCHDL') : null;
      filter === 'Requested for change' ? filtersCodes.push('RFC') : null;
      filter === 'Only Active' ? filtersCodes.push('ACTIVE') : null;
      filter === 'Not Allocated' ? filtersCodes.push('ALLOC') : null;
    });
    const params: Record<string, string> = {
      job_id: this.selected.jobIDs.data.join(','),
      dates: this.advanced ? this.dates.join(',') : this.datesIdSearch.join(','),
      service_code: this.selected.serviceCodes.data.join(','),
      driver_id: this.selected.driverIDs.data.join(','),
      customer_code: this.selected.customerCodes.data.join(','),
      state: this.selected.stateName.data.join(','),
      serial_number: this.selectedSingleInputs.serialNumbers.data.join(','),
      mixed_content: this.selectedSingleInputs.mixed.data.join(','),
      mixed_address: this.selectedSingleInputs.mixedAddress.data.join(','),
      extra: filtersCodes.join(','),
    };

    if (this.advanced && this.selectedSingleInputs.payOnHold.data.length > 0) {
      const payOnHold = this.selectedSingleInputs.payOnHold.data[0] === 'Yes' ? 'true' : 'false';
      params.pay_on_hold = payOnHold;
    }

    return params;
  }

  public find(immediate?: boolean): void {
    const params = this.buildSearchParams();
    this.addParamsToLocation(params);
    clearTimeout(this.timeout);

    // Search immediate for "Single" inputs (Serial number, Combined Address and Data)
    if (immediate) {
      this.findWrapper(params);
    } else {
      // Or 1.5 sec timeout for selects
      this.timeout = setTimeout(() => {
        this.findWrapper(params);
      }, 1500);
    }
  }

  private findWrapper(params) {
    this.loading = true;
    searchJobs(params)
      .then((data: IGetJobs) => {
        if (!this.loading) {
          this.searchedJobs = [];
        } else {
          this.searchedJobs = [...data.cancelled, ...data.completed, ...data.in_progress, ...data.unallocated];
        }
        this.numberOfJobs =
          data.cancelled.length + data.completed.length + data.in_progress.length + data.unallocated.length;
        this.isLimited = this.numberOfJobs === 100;
        if (this.isLimited) {
          Sentry.withScope(scope => {
            scope.setLevel('warning');
            Sentry.captureMessage('100 jobs limit search page');
          });
        }
      })
      .catch(err => {
        if (err.response?.status === 400) {
          Sentry.withScope(scope => {
            scope.setLevel('warning');
            Sentry.captureException(err);
          });
          this.$notify({
            group: 'network',
            title: 'Wrong request',
            text: 'Try to clarify the input data',
            type: 'warn',
          });
        } else {
          this.$notify({
            group: 'network',
            title: 'Something went wrong',
            text: notificationErrorText,
            type: 'warn',
          });
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public changeDate(dates: string[]) {
    this.dates = dates;
    this.isLimited = false;
    this.numberOfJobs = 0;
  }

  public changeDateIdSearch(dates: string[]) {
    this.datesIdSearch = dates;
    if (this.selected.jobIDs.data.length > 0) {
      this.find();
    }
  }

  public addInput(model: string, input: string[]) {
    input.forEach(item => {
      if (item && this.selected[model].data.indexOf(item) === -1) {
        this.selected[model].data.push(item);
      }
    });
  }

  public addInputSingle(model: string, input: string[]) {
    input.forEach(item => {
      if (item && this.selectedSingleInputs[model].data.indexOf(item) === -1) {
        this.selectedSingleInputs[model].data.push(item);
      }
    });
  }

  public addState(input: string[]) {
    const selectName = 'stateName';
    this.selected[selectName].data = input;
  }

  public payOnHoldChanged(input: string) {
    if (input) {
      const isPayOnHold = input === 'true';
      this.selectedSingleInputs.payOnHold.data = [isPayOnHold ? 'Yes' : 'No'];
      if (isPayOnHold) {
        const activeFilterIndex = this.selected.filters.data.indexOf('Only Active');
        if (activeFilterIndex !== -1) {
          this.selected.filters.data.splice(activeFilterIndex, 1);
        }
      }
    }
  }

  public openMultipleSearch() {
    this.modalMupltipleSearch.isOpen = true;
    this.advanced = true;
  }

  public filtersChanged(filters) {
    filters.forEach(item => {
      if (item && this.selected.filters.data.indexOf(item) === -1) {
        this.selected.filters.data.push(item);
      }
    });
  }

  public deleteItems(model: string, difference: string) {
    this.selected[model].data = this.selected[model].data.filter(item => item !== difference);
  }

  public deleteInput(model: string, idx: number) {
    if (model === 'serialNumbers' || model === 'mixed' || model === 'mixedAddress' || model === 'payOnHold') {
      this.selectedSingleInputs[model].data.splice(idx, 1);
    } else {
      this.selected[model].data.splice(idx, 1);
    }
    const params = {
      job_id: this.selected.jobIDs.data.join(','),
      dates: this.dates.join(','),
      service_code: this.selected.serviceCodes.data.join(','),
      driver_id: this.selected.driverIDs.data.join(','),
      customer_code: this.selected.customerCodes.data.join(','),
      state_name: this.selected.stateName.data.join(','),
      serial_number: this.selectedSingleInputs.serialNumbers.data.join(','),
      mixed_content: this.selectedSingleInputs.mixed.data.join(','),
      mixed_address: this.selectedSingleInputs.mixedAddress.data.join(','),
    };
    this.addParamsToLocation(params);
  }

  // watch to switch advanced search
  @Watch('advanced')
  private watchToAdvanced() {
    if (this.advanced) {
      this.selected.jobIDs.data = [];
      if (this.selected.filters.data.indexOf('Only Active') === -1) {
        this.selected.filters.data.push('Only Active');
      }
      if (Object.keys(this.$route.query).indexOf('dates') === -1) {
        const userTimeZone = this.user.timezone || timeZoneDefault;
        const fromDate = format(utcToZonedTime(addMonths(new Date(), -6), userTimeZone), 'yyyy-MM-dd'); // - 6 month
        const toDate = format(utcToZonedTime(addMonths(new Date(), 6), userTimeZone), 'yyyy-MM-dd'); // + 6 month
        this.dates = [fromDate, toDate];
      } else {
        this.dates = (this.$route.query.dates as string).split(',');
      }
    } else {
      this.selected.driverIDs.data = [];
      this.selected.serviceCodes.data = [];
      this.selected.customerCodes.data = [];
      this.selected.stateName.data = [];
      this.selectedSingleInputs.serialNumbers.data = [];
      this.selectedSingleInputs.mixed.data = [];
      this.selectedSingleInputs.mixedAddress.data = [];
      this.selectedSingleInputs.payOnHold.data = [];
      this.selected.filters.data = [];
      this.isLimited = false;
      this.numberOfJobs = 0;
    }
  }

  // click to clear filters
  public clearFilters() {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    const fromDate = format(utcToZonedTime(addMonths(new Date(), -6), userTimeZone), 'yyyy-MM-dd'); // - 6 month
    const toDate = format(utcToZonedTime(addMonths(new Date(), 6), userTimeZone), 'yyyy-MM-dd'); // + 6 month
    this.dates = [fromDate, toDate];
    this.selected.jobIDs.data = [];
    this.selected.driverIDs.data = [];
    this.selected.serviceCodes.data = [];
    this.selected.customerCodes.data = [];
    this.selected.stateName.data = [];
    this.selectedSingleInputs.serialNumbers.data = [];
    this.selectedSingleInputs.mixed.data = [];
    this.selectedSingleInputs.mixedAddress.data = [];
    this.selectedSingleInputs.payOnHold.data = [];
    this.selected.filters.data = [];
    this.isLimited = false;
    this.numberOfJobs = 0;
    this.rulesForFind();
  }

  private addParamsToLocation(params) {
    const query = {};
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        if (params[key]) {
          query[key] = params[key];
        }
      }
    }
    this.$router.push({ path: 'search', query: { ...query } }).catch(() => false);
  }

  private created(): void {
    // search route query watcher:
    this.$watch(
      () => this.$route.query,
      (toParams, previousParams) => {
        if (toParams.job_id && previousParams.job_id && typeof toParams.job_id === 'string') {
          this.selected.jobIDs.data = toParams.job_id.split(',');
          this.find();
        }
      },
    );
    // Default dates (+/- 6 month)
    this.setDefaultDatesRange();
    // URL parsing
    if (Object.keys(this.$route.query).length !== 0) {
      Object.keys(this.$route.query).forEach(key => {
        if (key === 'job_id') {
          this.selected.jobIDs.data = (this.$route.query.job_id as string).split(',');
        }
        if (key === 'dates') {
          const dateString = (this.$route.query.dates as string).split(',');
          if (
            isValid(dp(dateString[0], 'yyyy-MM-dd', new Date())) &&
            isValid(dp(dateString[1], 'yyyy-MM-dd', new Date()))
          ) {
            this.dates = (this.$route.query.dates as string).split(',');
          } else {
            // date string has one date, "YYYY-MM-DD" format, copy the second date from the first
            if (isValid(dp(dateString[0], 'yyyy-MM-dd', new Date()))) {
              this.dates[0] = dateString[0];
              if (!dateString[1]) {
                this.dates[1] = this.dates[0];
              }
            } else {
              // no dates at all – plus/minus 6 months
              const userTimeZone = this.user.timezone || timeZoneDefault;
              const fromDate = format(utcToZonedTime(addMonths(new Date(), -6), userTimeZone), 'yyyy-MM-dd'); //-6 month
              const toDate = format(utcToZonedTime(addMonths(new Date(), 6), userTimeZone), 'yyyy-MM-dd'); // +6 month
              this.dates = [fromDate, toDate];
            }
          }
          this.datesIdSearch = this.dates;
        }
        if (
          key === 'driver_id' ||
          key === 'customer_code' ||
          key === 'service_code' ||
          key === 'serial_number' ||
          key === 'state_name' ||
          key === 'mixed_content' ||
          key === 'mixed_address' ||
          key === 'pay_on_hold' ||
          key === 'extra'
        ) {
          this.advanced = true;
        }
        if (key === 'driver_id') {
          this.selected.driverIDs.data = (this.$route.query.driver_id as string).split(',');
        }
        if (key === 'customer_code') {
          this.selected.customerCodes.data = (this.$route.query.customer_code as string).split(',');
        }
        if (key === 'service_code') {
          this.selected.serviceCodes.data = (this.$route.query.service_code as string).split(',');
        }
        if (key === 'state') {
          this.selected.stateName.data = (this.$route.query.state as string).split(',');
        }
        if (key === 'serial_number') {
          this.selectedSingleInputs.serialNumbers.data = (this.$route.query.serial_number as string).split(',');
        }
        if (key === 'mixed_content') {
          this.selectedSingleInputs.mixed.data = (this.$route.query.mixed_content as string).split(',');
        }
        if (key === 'mixed_address') {
          this.selectedSingleInputs.mixedAddress.data = (this.$route.query.mixed_address as string).split(',');
        }
        if (key === 'pay_on_hold') {
          this.selectedSingleInputs.payOnHold.data = (this.$route.query.pay_on_hold as string).split(',');
        }
        if (key === 'extra') {
          const filtersCodes = (this.$route.query.extra as string).split(',');
          const filters: string[] = [];
          filtersCodes.map(code => {
            code === 'CNTSCHDL' && this.$ability.can('staff', 'Job') ? filters.push("Can't schedule") : null; // hide for non-staff users
            code === 'RFC' ? filters.push('Requested for change') : null;
            code === 'ACTIVE' ? filters.push('Only Active') : null;
            code === 'ALLOC' ? filters.push('Not Allocated') : null;
          });
          this.selected.filters.data = filters;
        }
      });
    }
  }

  /* taken from https://www.cyberforum.ru/javascript-beginners/thread2271518.html
   */
  public copyURL() {
    const copyText = document.createElement('input');
    copyText.value = window.location.href;
    document.body.appendChild(copyText);
    copyText.select();
    document.execCommand('copy');
    document.body.removeChild(copyText);
    this.$notify({
      group: 'network',
      text: 'Link copied',
      type: 'success',
      duration: 3000,
    });
  }

  public exportToPDF(): void {
    const params = this.buildSearchParams();

    this.loadingPdf = true;
    this.loading = true;
    searchJobsPDF(params)
      .then(response => {
        blobDownloader(response);
        this.$notify({
          group: 'network',
          text: 'PDF export completed',
          type: 'success',
          duration: 3000,
        });
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'PDF export failed',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.captureException(err);
      })
      .finally(() => {
        this.loadingPdf = false;
        this.loading = false;
      });
  }
}
